import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MaterialPickupModel } from '../../components/material-pickup/material-pickup.model';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { EXPANDS } from '../../enums/expands.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { OdataResponse } from '../../interfaces/online-only.interface';
import { CseDopModel, ODataCseDopModel } from '../../models/complex/cse-dop.model';
import {
    MaterialBillingIndicatorModel,
    ODataMaterialBillingIndicatorModel,
} from '../../models/complex/material-billing-indicator.model';
import { MaterialCentralReturnModel } from '../../models/complex/material-central-return.model';
import {
    CseStockModel,
    MaterialInventoryModel,
    ODataCseStockModel,
} from '../../models/complex/material-inventory.model';
import { MaterialOrderModel } from '../../models/complex/material-order.model';
import { MaterialQuoteModel } from '../../models/complex/material-quote.model';
import {
    MaterialStockModel,
    ODataMaterialStockModel,
    ODataMaterialStockResponse,
} from '../../models/complex/material-stock.model';
import { UserModel } from '../../models/complex/user.model';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { isDefined } from '../../utilities/is-defined';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { OdataService } from '../odata/odata.service';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { OdataMaterialInterface } from './odata-material.interface';

@Injectable({
    providedIn: 'root',
})
export class OdataMaterialService implements OdataMaterialInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly odataService: OdataService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly http: HttpClient,
        private readonly uriUtilities: UriUtilitiesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public getCseDop(): Observable<CseDopModel[]> {
        const baseUrl = this.uriUtilities.getUriForContent() + ENDPOINTS.CseDopSet;

        return this.http.get<OdataResponse<ODataCseDopModel>>(baseUrl).pipe(
            map((response) =>
                ODataUtilities.transformResponseDataToArray(response.d)
                    .map((value: ODataCseDopModel) => CseDopModel.fromOData(value))
                    .filter(isDefined),
            ),
            catchError((err) => {
                this.log.error('getCseDop() - request error:  ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public getMaterialBootStock(userData: UserModel): Observable<MaterialStockModel> {
        let query = this.uriUtilities.getUriForContent() + ENDPOINTS.MaterialStockSet;

        const expand = EXPANDS.MaterialStockAvailability;
        const format = '$format=json';

        query = this.uriUtilities.appendParameter(query, expand);
        query = this.uriUtilities.appendUriFilters(query, userData);
        query = this.uriUtilities.appendParameter(query, format);

        return this.http.get<ODataMaterialStockResponse>(query).pipe(
            map((response) => MaterialStockModel.fromODataMaterialStockModel(response.d.results)),
            catchError((err) => {
                this.log.error('getBootStock() - request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public saveMaterialPickup(materialPickup: MaterialPickupModel): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            materialPickup,
            ENDPOINTS.MaterialPickupHeaders,
            `${materialPickup.notificationIdentifierModel.toString()}-${getUuidV4()}`,
        );
    }

    public saveMaterialReturn(materialReturn: MaterialCentralReturnModel): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            materialReturn,
            ENDPOINTS.MaterialReturnHeaders,
            `${materialReturn.notificationIdentifierModel.toString()}-${getUuidV4()}`,
        );
    }

    public saveMaterialOrder(materialOrder: MaterialOrderModel): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            materialOrder,
            ENDPOINTS.MaterialOrder,
            `${materialOrder.notificationModel.identifier.toString()}-${getUuidV4()}`,
        );
    }

    public saveMaterialQuote(materialQuote: MaterialQuoteModel): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            materialQuote,
            ENDPOINTS.MaterialQuote,
            `${materialQuote.notificationModel?.identifier.toString()}-${getUuidV4()}`,
        );
    }

    public getMaterialBillingIndicators(
        model: MaterialBillingIndicatorModel,
    ): Observable<MaterialBillingIndicatorModel> {
        const outModel = model.getOutModel();

        return this.onlineOnlyService
            .post(ENDPOINTS.MaterialBillingIndicatorSet, outModel)
            .pipe(
                map((response: ODataMaterialBillingIndicatorModel) =>
                    MaterialBillingIndicatorModel.fromOdata(response),
                ),
            );
    }

    public getMaterialInventory(selectedUserModel: UserModel): Observable<MaterialInventoryModel> {
        let query = ENDPOINTS.CseStockSet.toString();
        const filter = `FrontId eq '${selectedUserModel.getFrontId()}' and Plant eq '${selectedUserModel.getPlant()}' and Material eq '' and SalesOrg eq '${selectedUserModel.getSalesOrg()}' and StorageLoc eq ''`;
        const expand = EXPANDS.CseStock;
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, expand);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get(query).pipe(
            map((response: ODataCseStockModel[]) => {
                const cseStockList = response[0];
                const materialInventoryList: CseStockModel[] = [];
                cseStockList.CSEStockAvailability.results.map((cseStock) =>
                    materialInventoryList.push(new CseStockModel(cseStock)),
                );

                return new MaterialInventoryModel(materialInventoryList);
            }),
        );
    }

    public getMaterialStock(
        frontId: string,
        materialNr: string,
        storageLocation: string,
    ): Observable<MaterialStockModel> {
        let query = ENDPOINTS.MaterialStockSet.toString();
        const frontIdFilter = `FrontId eq '${frontId}'`;
        const materialFilter = `Material eq '${materialNr}'`;
        const sLocFilter = `StorageLoc eq '${storageLocation}' `;

        query = this.uriUtilities.appendFilterValue(query, frontIdFilter);
        query = this.uriUtilities.appendFilterValue(query, materialFilter);

        if (this.clientManager.isNgmP58() && storageLocation) {
            query = this.uriUtilities.appendFilterValue(query, sLocFilter);
        }

        const expand = EXPANDS.MaterialStockAvailability;
        const format = '$format=json';
        query = this.uriUtilities.appendParameter(query, expand);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService
            .get(query)
            .pipe(
                map((response: ODataMaterialStockModel[]) => MaterialStockModel.fromODataMaterialStockModel(response)),
            );
    }
}
